import { formatPriceByCurrency } from "./formatters";
import { PURCHASE_ORDER_SHIPMENT_STATUS_LABELS } from "../constants/OrderConstant";

/**
 * Mag de mail naar de boekhouder eruit, en zo nee: wat staat er nog open (BMX-23).
 *
 * De mail bevat onderaan een controleblok "totaal gefactureerd vs. totaal betaald". Dat
 * blok klopt alleen als de zending écht af is; anders krijgt de boekhouder een overzicht
 * met een verschil dat hij niet kan afstemmen. Vandaar dat het versturen geblokkeerd is
 * tot alle punten hieronder groen staan.
 *
 * ── De rekensom moet gelijk zijn aan die van de mail ──
 *
 * De mail wordt gebouwd in `api` (EmailService#getShipmentCompleteMailTemplate) en rekent:
 *
 *     openstaand = definitieve factuur − (betalingen op die factuur + betalingen op de
 *                                         gekoppelde pro forma's)
 *
 * Een pro forma is de aanbetaling op dezelfde goederen en wordt tegen de definitieve
 * factuur verrekend — het bedrag ervan telt dus níét op bij het factuurtotaal, de
 * betalingen erop tellen wél mee als betaling. Dat is ook hoe het CMS zelf `amountDue`
 * van een definitieve factuur berekent (zie `recomputeFinalInvoiceTotals` in
 * purchase-order-invoices/detail). Wijk je hier van af, dan kan deze checklist groen
 * staan terwijl de mail een verschil laat zien — precies wat BMX-23 moest voorkomen.
 *
 * Let op: betalingen die nog op de inkooporder zelf staan (de "legacy"-betalingen) tellen
 * hier bewust niet mee, omdat de mail ze ook niet ophaalt — die kent alleen betalingen die
 * aan een factuur hangen. Zo'n zending wordt dus geblokkeerd, en dat is terecht: de mail
 * zou er anders uitgaan met een openstaand bedrag dat allang betaald is.
 */

/** Marge voor centenverschil bij het vergelijken van bedragen. */
const AMOUNT_TOLERANCE = 0.01;

/** De zending is de goederenontvangst doorgelopen: geteld en verwerkt. */
const SHIPMENT_PROCESSED = "completed";

/** Wat er betaald is op de definitieve factuur plus de gekoppelde pro forma's. */
export const getPaidTotal = (invoicePayments) =>
  (invoicePayments || []).reduce((sum, payment) => sum + (payment?.amount || 0), 0);

/** Wat er nog openstaat: factuurbedrag minus alles wat erop betaald is. Negatief = te veel betaald. */
export const getOutstandingAmount = (invoice, invoicePayments) =>
  Math.round(((invoice?.totalAmount || 0) - getPaidTotal(invoicePayments)) * 100) / 100;

/**
 * Pro forma's van de bronorders van deze zending die nog nergens tegenover staan.
 * Zonder koppeling aan de definitieve factuur telt hun aanbetaling niet mee, en ontbreekt
 * ze dus ook in de mail.
 */
export const getUnsettledProformas = (supplierInvoices, sourceOrderIds) =>
  (supplierInvoices || []).filter(
    (invoice) =>
      invoice &&
      invoice.type === "proforma" &&
      !invoice.linkedFinalInvoiceId &&
      invoice.purchaseOrderId &&
      (sourceOrderIds || []).includes(invoice.purchaseOrderId),
  );

/**
 * De volledige checklist, in de volgorde waarin hij getoond wordt.
 * Elk punt: `{ key, label, ok, detail }` — `detail` is de toelichting eronder, of null.
 */
export const getAccountantMailChecks = ({
  shipment,
  invoice,
  invoicePayments,
  supplierInvoices,
  sourceOrderIds,
  costPriceHistory,
  currency,
  formatTimestamp,
}) => {
  if (!shipment) return [];

  const paidTotal = getPaidTotal(invoicePayments);
  const outstanding = getOutstandingAmount(invoice, invoicePayments);
  const hasInvoiceAmount = !!invoice && (invoice.totalAmount || 0) > 0;
  const paymentsWithoutRate = (invoicePayments || []).filter((payment) => !payment?.exchangeRate);
  const unsettledProformas = getUnsettledProformas(supplierInvoices, sourceOrderIds);
  const money = (value) => formatPriceByCurrency(value, currency);

  return [
    {
      key: "received",
      label: "Verzending is volledig ontvangen en afgerond",
      ok: shipment.status === SHIPMENT_PROCESSED,
      detail:
        shipment.status === SHIPMENT_PROCESSED
          ? null
          : `Status is nu "${PURCHASE_ORDER_SHIPMENT_STATUS_LABELS[shipment.status] || shipment.status}"`,
    },
    {
      key: "stock-count",
      label: "Voorraadtelling aangemaakt in Exact",
      ok: !!shipment.exactStockCountId,
      detail: shipment.exactStockCountId ? null : "Nog niet gesynchroniseerd naar Exact",
    },
    {
      key: "invoice",
      label: "Definitieve factuur gekoppeld",
      ok: !!invoice,
      detail: invoice ? invoice.invoiceNumber || null : "Geen definitieve factuur aan deze verzending gekoppeld",
    },
    {
      key: "invoice-amount",
      label: "Factuur heeft een totaalbedrag",
      ok: hasInvoiceAmount,
      detail: hasInvoiceAmount ? money(invoice.totalAmount) : "Totaalbedrag is 0 — factuurregels ontbreken",
    },
    {
      key: "paid",
      label: "Factuur volledig betaald (inclusief aanbetaling)",
      ok: hasInvoiceAmount && Math.abs(outstanding) < AMOUNT_TOLERANCE,
      detail: !hasInvoiceAmount
        ? "Kan niet bepaald worden zonder factuurbedrag"
        : Math.abs(outstanding) < AMOUNT_TOLERANCE
          ? `${money(paidTotal)} betaald`
          : outstanding > 0
            ? `Nog openstaand: ${money(outstanding)}`
            : `Te veel betaald: ${money(Math.abs(outstanding))}`,
    },
    {
      key: "proformas-settled",
      label: "Pro forma's verrekend met de definitieve factuur",
      ok: unsettledProformas.length === 0,
      detail:
        unsettledProformas.length === 0
          ? null
          : `${unsettledProformas.length} pro forma zonder koppeling: ${unsettledProformas
              .map((proforma) => proforma.invoiceNumber || proforma.id)
              .join(", ")}`,
    },
    // De mail rekent alles om naar euro's met de gewogen koers van de betalingen. Een
    // betaling zonder koers telt daar niet in mee, waardoor de euro-bedragen scheef gaan.
    // Bij een factuur in euro's speelt dat niet.
    ...((currency || "USD") !== "EUR"
      ? [
          {
            key: "exchange-rates",
            label: "Alle betalingen hebben een wisselkoers",
            ok: paymentsWithoutRate.length === 0,
            detail:
              paymentsWithoutRate.length === 0
                ? null
                : `${paymentsWithoutRate.length} betaling(en) zonder koers — de euro-bedragen in de mail kloppen dan niet`,
          },
        ]
      : []),
    {
      key: "cost-prices",
      label: "Kostprijzen bijgewerkt",
      ok: !!costPriceHistory,
      detail: costPriceHistory
        ? `Bijgewerkt op ${formatTimestamp ? formatTimestamp(costPriceHistory.performedAt) : costPriceHistory.performedAt}`
        : "Nog geen kostprijsupdate voor deze verzending vastgelegd",
    },
  ];
};

/** De punten die nog openstaan. Leeg = de mail mag eruit. */
export const getAccountantMailBlockers = (checks) => (checks || []).filter((check) => !check.ok);

/** Mag de mail verstuurd worden? */
export const canSendAccountantMail = (checks) => getAccountantMailBlockers(checks).length === 0;
